import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  getQuizById,
  getQuizLinksByQuizId,
  generateQuizLink,
  type Quiz,
  type QuizLink
} from '../../services/quizService';

interface QuizLinkGeneratorProps {
  quizId: string;
  onBack: () => void;
}

const QuizLinkGenerator: React.FC<QuizLinkGeneratorProps> = ({ quizId, onBack }) => {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [links, setLinks] = useState<QuizLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [expiryHours, setExpiryHours] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  useEffect(() => {
    loadQuizAndLinks();
  }, [quizId]);

  const loadQuizAndLinks = async () => {
    try {
      setLoading(true);
      const [quizData, linksData] = await Promise.all([
        getQuizById(quizId),
        getQuizLinksByQuizId(quizId)
      ]);
      setQuiz(quizData);
      setLinks(linksData);
      setError(null);
    } catch (err) {
      console.error('Error loading quiz and links:', err);
      setError('Failed to load quiz data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!participantName.trim()) {
      setError('Participant name is required');
      return;
    }

    try {
      setGenerating(true);
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + expiryHours);

      await generateQuizLink(quizId, participantName, expiresAt);
      setParticipantName('');
      setExpiryHours(2);
      setGenerateDialogOpen(false);
      await loadQuizAndLinks();
      setError(null);
    } catch (err) {
      console.error('Error generating link:', err);
      setError('Failed to generate quiz link');
    } finally {
      setGenerating(false);
    }
  };

  const getQuizUrl = (token: string): string => {
    return `${window.location.origin}/quiz/${token}`;
  };

  const handleCopyLink = async (token: string) => {
    try {
      await navigator.clipboard.writeText(getQuizUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      console.error('Error copying link:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date();
  };

  const isCompleted = (link: QuizLink): boolean => {
    return !!link.completed_at;
  };

  const getLinkStatus = (link: QuizLink): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
    if (isCompleted(link)) {
      return { label: 'Completed', color: 'success' };
    }
    if (isExpired(link.expires_at)) {
      return { label: 'Expired', color: 'error' };
    }
    return { label: 'Active', color: 'warning' };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!quiz) {
    return (
      <Box>
        <Alert severity="error">Quiz not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
          Back to Quizzes
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            {quiz.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate Quiz Links
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setGenerateDialogOpen(true)}
        >
          Generate Link
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Participant</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Expires</strong></TableCell>
              <TableCell><strong>Completed</strong></TableCell>
              <TableCell><strong>Time Taken</strong></TableCell>
              <TableCell align="right"><strong>Link</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No links generated yet. Click "Generate Link" to create one!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              links.map((link) => {
                const status = getLinkStatus(link);
                return (
                  <TableRow key={link.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {link.participant_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={status.label} color={status.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(link.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(link.expires_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {link.completed_at ? formatDate(link.completed_at) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {link.time_taken_minutes ? `${link.time_taken_minutes.toFixed(1)} min` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color={copiedToken === link.link_token ? 'success' : 'primary'}
                        onClick={() => handleCopyLink(link.link_token)}
                        title="Copy Link"
                      >
                        {copiedToken === link.link_token ? <CheckIcon /> : <ContentCopyIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Statistics */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Total Links
            </Typography>
            <Typography variant="h5">
              {links.length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
            <Typography variant="h5" color="success.main">
              {links.filter(l => isCompleted(l)).length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Active
            </Typography>
            <Typography variant="h5" color="warning.main">
              {links.filter(l => !isCompleted(l) && !isExpired(l.expires_at)).length}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Expired
            </Typography>
            <Typography variant="h5" color="error.main">
              {links.filter(l => !isCompleted(l) && isExpired(l.expires_at)).length}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Generate Link Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => !generating && setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Quiz Link</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Participant Name"
            type="text"
            fullWidth
            variant="outlined"
            value={participantName}
            onChange={(e) => setParticipantName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            helperText="Enter the name of the person taking this quiz"
          />

          <FormControl fullWidth variant="outlined">
            <InputLabel>Expiry Time</InputLabel>
            <Select
              value={expiryHours}
              onChange={(e) => setExpiryHours(e.target.value as number)}
              label="Expiry Time"
              startAdornment={
                <InputAdornment position="start">
                  <AccessTimeIcon />
                </InputAdornment>
              }
            >
              <MenuItem value={1}>1 hour</MenuItem>
              <MenuItem value={2}>2 hours (default)</MenuItem>
              <MenuItem value={3}>3 hours</MenuItem>
              <MenuItem value={6}>6 hours</MenuItem>
              <MenuItem value={12}>12 hours</MenuItem>
              <MenuItem value={24}>24 hours</MenuItem>
              <MenuItem value={48}>48 hours</MenuItem>
              <MenuItem value={168}>1 week</MenuItem>
            </Select>
          </FormControl>

          <Alert severity="info" sx={{ mt: 2 }}>
            The generated link will be unique to this participant and will expire after the selected time period.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button onClick={handleGenerateLink} variant="contained" disabled={generating}>
            {generating ? 'Generating...' : 'Generate Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuizLinkGenerator;
